import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import { Badge } from "@/components/ui/badge";
import { Trophy, TrendingUp, Loader2, Crown, Medal } from "lucide-react";

const fmt = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(n ?? 0);
};

const rankStyle = (rank) => {
  if (rank === 1) return { ring: "border-chart-4/50 bg-chart-4/5", badge: "bg-chart-4 text-background", icon: Crown, color: "text-chart-4" };
  if (rank === 2) return { ring: "border-white/20 bg-white/5", badge: "bg-white/80 text-background", icon: Medal, color: "text-white/80" };
  if (rank === 3) return { ring: "border-secondary/40 bg-secondary/5", badge: "bg-secondary text-white", icon: Medal, color: "text-secondary" };
  return { ring: "border-border/50", badge: "bg-white/5 text-muted-foreground", icon: null, color: "text-muted-foreground" };
};

const Row = ({ e, isMe, i }) => {
  const rs = rankStyle(e.rank);
  const Icon = rs.icon;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.04, 0.4) }}
      className={`flex items-center gap-4 rounded-xl border p-4 ${rs.ring} ${isMe ? "ring-1 ring-primary" : ""}`} data-testid={`leaderboard-row-${e.rank}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-heading font-black shrink-0 ${rs.badge}`}>
        {Icon ? <Icon className="w-5 h-5" /> : e.rank}
      </div>
      <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center text-primary font-bold shrink-0">
        {(e.name?.[0] || "C").toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold truncate flex items-center gap-2">
          {e.name} {isMe && <span className="text-[10px] font-bold uppercase tracking-wider text-primary">You</span>}
        </div>
        <div className="text-xs text-muted-foreground truncate">@{e.handle} · {e.platform_name}{e.niche ? ` · ${e.niche}` : ""}</div>
      </div>
      <div className="text-right hidden sm:block">
        <div className="text-xs text-muted-foreground">Total</div>
        <div className="font-mono font-semibold">{fmt(e.followers)}</div>
      </div>
      <div className="text-right">
        <div className="text-xs text-muted-foreground">This month</div>
        <div className="font-heading font-black text-primary flex items-center gap-1 justify-end"><TrendingUp className="w-3.5 h-3.5" />+{fmt(e.month_gained)}</div>
      </div>
    </motion.div>
  );
};

const Leaderboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leaderboard").then((r) => setData(r.data)).catch(() => toast.error("Could not load leaderboard")).finally(() => setLoading(false));
  }, []);

  const board = data?.leaderboard || [];
  const me = data?.me;
  const meInTop = me && board.some((e) => e.user_id === me.user_id);

  return (
    <DashboardShell>
      <div className="p-6 md:p-10 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-chart-4/15 text-chart-4 flex items-center justify-center"><Trophy className="w-5 h-5" /></div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Creator Leaderboard</h1>
        </div>
        <p className="text-muted-foreground mb-8">Biggest follower gains this {data?.month ? `— ${data.month}` : "month"}. Keep posting to climb the ranks.</p>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
        ) : board.length === 0 ? (
          <div className="rounded-2xl border border-border/50 bg-card p-12 text-center text-muted-foreground" data-testid="leaderboard-empty">
            No active campaigns yet. Launch a plan to appear on the leaderboard!
          </div>
        ) : (
          <div className="space-y-3" data-testid="leaderboard-list">
            {board.map((e, i) => <Row key={e.user_id} e={e} isMe={me && e.user_id === me.user_id} i={i} />)}
            {me && !meInTop && (
              <>
                <div className="text-center text-xs text-muted-foreground py-2">· · ·</div>
                <Row e={me} isMe i={board.length} />
              </>
            )}
          </div>
        )}
      </div>
    </DashboardShell>
  );
};

export default Leaderboard;
